import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AuthState,
  IgnoredError,
  ReportError,
  useAuth,
  useMutation,
  useQuery,
} from '@furality/frontend-stack';
import { Button, Foldable, Slice } from '@furality/furality-purpose';
import type {
  RegistrationFormDto,
  RegistrationUpdateFormDto,
} from '@furality/registration-client/models';
import { BOOP_HOST } from '#/api/boop.ts';
import registration from '#/api/registration.ts';
import useDiscord from '#/hooks/useDiscord.ts';
import type Navigation from '@/components/_register/Navigation.ts';
import Step from '@/components/_register/Step.ts';
import AdditionalInformation from '@/components/_register/steps/AdditionalInformation.tsx';
import Addons from '@/components/_register/steps/AddOns.tsx';
import Agreement from '@/components/_register/steps/Agreement.tsx';
import Discord from '@/components/_register/steps/Discord.tsx';
import Introduction from '@/components/_register/steps/Introduction.tsx';
import IntroductionPassed from '@/components/_register/steps/IntroductionPassed.tsx';
import JoinOurDiscord from '@/components/_register/steps/JoinOurDiscord.tsx';
import Payment from '@/components/_register/steps/Payment.tsx';
import Pricing from '@/components/_register/steps/Pricing.tsx';
import PricingPassed from '@/components/_register/steps/PricingPassed.tsx';
import RegistrationComplete from '@/components/_register/steps/RegistrationComplete.tsx';
import RegistrationSummary from '@/components/_register/steps/Summary.tsx';
import useRegistrationNavigation from '@/components/_register/useRegistrationNavigation.ts';
import { RegisterLayout } from '@/components/layouts/index.tsx';
import InlineLoader from '@/components/loading/InlineLoader.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useCart from '@/lib/useCart.tsx';
import useToasts from '@/lib/useToast.ts';

import type RegFormData from '../components/_register/RegFormData.ts';
import AgeCheck from '../components/_register/steps/AgeCheck.tsx';
import Title from '../components/_register/steps/Title.tsx';
import useFlag from '../lib/hooks/useFlag.ts';

type RegistrationFormErrorFallbackProps = {
  retry: () => void;
  loading: boolean;
};

const RegistrationFormErrorFallback: FC<RegistrationFormErrorFallbackProps> = ({
  retry,
  loading,
}) => {
  const { t } = useTranslation(['common', 'register']);

  return (
    <div className="text-center py-4">
      <h2 className="mb-5">{t('register:could_not_get_reg')}</h2>
      {loading ? <InlineLoader /> : <Button onClick={retry}>{t('retry')}</Button>}
    </div>
  );
};

const clearAgeCheck = () => localStorage.removeItem('f9.registration.legalAge');

const processRegForm = (data: RegFormData): RegistrationFormDto => {
  const tmp: RegistrationFormDto = {
    ...data,
    isAdult: data.isAdult === true,
  };

  delete (tmp as Record<string, unknown> & { selectedCurrency?: unknown }).selectedCurrency;

  return tmp;
};

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const Registration = () => {
  const auth = useAuth();
  const discord = useDiscord(BOOP_HOST);
  const { clearCart, regInCart, cart, checkoutCreate, refreshCart } = useCart();
  const queryClient = useQueryClient();

  const { step, setStep, prevStep, nextStep } = useRegistrationNavigation();

  const { regStatus } = useCart();

  const featuresReady = useFlagsStatus().flagsReady;
  const regIsOpen = useFlag('registration');
  const enableDiscordLink = useFlag('discordLinking');

  const form = useForm<RegFormData>({
    defaultValues: {
      email: '',
      platform: 'Unknown',
      marketingConsent: false,
      isAdult: null,
      selectedCurrency: 'USD',
    },
  });
  const { setValue, getValues, reset: formReset } = form;

  const { t } = useTranslation(['common', 'register']);
  const { pushToast } = useToasts();

  const handleRegFormResponse = (response: RegistrationFormDto) => {
    const data = response;

    setValue('country', data.country);
    setValue('isAdult', data.isAdult);
    setValue('platform', data.platform);
    setValue('marketingConsent', data.marketingConsent);
    setValue('email', data.email);
  };

  const postRegForm = useMutation({
    mutationFn: async (data: RegistrationUpdateFormDto) => {
      const response = await registration.value.registrationUpdateForm({ body: data });

      switch (response.status) {
        case 201:
          auth.renew();
          await getRegForm.refetch();
          return response;
        case 401:
          restartRegistration();
          return null;
        default:
          pushToast('error', t('register:errors.unknown_error'));
          throw new ReportError(response);
      }
    },
  });

  const getRegForm = useQuery({
    queryKey: ['regForm'],
    queryFn: async () => {
      const response = await registration.value.registrationGetForm();
      switch (response.status) {
        case 200:
          handleRegFormResponse(response.data);
          return response.data;
        case 401:
          return undefined;
        default:
          // TODO: missing API status in spec!
          if (response.response.status === 404) {
            return null;
          }

          throw new ReportError(response);
      }
    },
    enabled: featuresReady && auth.state === 'loggedIn' && regIsOpen,
  });

  const isReady =
    auth.state !== AuthState.Loading &&
    (auth.state !== AuthState.LoggedIn ||
      (cart.isFetched &&
        regStatus.isFetched &&
        getRegForm.isFetched &&
        discord.link.status !== 'pending'));

  // this runs only once
  const isMounted = useRef(false);
  useEffect(() => {
    const isRegSubmitted =
      regStatus.data?.status === 'confirmed' || regStatus.data?.status === 'processing';

    if (isMounted.current) {
      return;
    }

    if (isReady) {
      isMounted.current = true;
      if (isRegSubmitted) {
        setStep(!discord.user?.handle ? Step.Discord : Step.Complete);
      } else if (getRegForm.data && regInCart) {
        setStep(Step.Payment);
      } else {
        setStep(auth.state === 'loggedIn' ? Step.LoadingForm : Step.StartPage);
      }
    }
  }, [
    regInCart,
    setStep,
    auth.state,
    regStatus.data?.status,
    getRegForm.data,
    discord.user,
    isReady,
  ]);

  const submit = async () => {
    if (!cart.data) {
      return;
    }
    const total = cart.data.total;
    await postRegForm.mutateAsync(processRegForm(getValues()));
    if (!total) {
      await checkoutCreate.mutateAsync('none');
      setStep(Step.Complete);
    } else {
      nextStep();
    }
  };

  const restartRegistration = async () => {
    formReset();
    await clearCart.mutateAsync();
    queryClient.removeQueries({ queryKey: ['cart', 'order'] });
    await refreshCart();

    setStep(auth.state === AuthState.LoggedIn ? Step.PricingTable : Step.StartPage);
  };

  const nukeReg = useMutation({
    mutationFn: async () => {
      if (!auth.user?.id) {
        return;
      }
      // this only works on dev
      const res = await registration.value.registrationAdminDeleteUser({
        params: {
          userId: auth.user.id,
        },
      });

      clearAgeCheck();

      switch (res.status) {
        case 200: {
          window.location.reload();
          return;
        }
        default:
          throw new IgnoredError(res);
      }
    },
  });

  useEffect(() => {
    if (step === Step.LoadingForm && regStatus.isFetched) {
      nextStep();
    }
  }, [step, regStatus.isFetched, nextStep]);

  const isAdult = getValues('isAdult');

  useEffect(() => {
    if (step === Step.AgeCheck && isAdult) {
      nextStep();
    }
  }, [step, isAdult, nextStep]);

  if (!featuresReady || !isReady) {
    return <PageLoading />;
  }

  if (!regIsOpen) {
    return (
      <Slice size="lg">
        <h3 className="mt-4">{t('shared.registration_closed')}</h3>
      </Slice>
    );
  }

  const navigation: Navigation = {
    nextStep,
    prevStep,
    step,
    setStep,
  };

  return (
    <FormProvider {...form}>
      <div
        className="d-flex flex-column gap-4 justify-content-center mt-5"
        style={{
          minHeight: '80vh',
        }}
      >
        <div>
          <Title passed={step >= Step.StartPage}>{t('register:introduction.title')}</Title>
          {step === Step.StartPage && <Introduction />}
          {step >= Step.StartPage && auth.state === 'loggedIn' && <IntroductionPassed />}
        </div>

        {step === Step.LoadingForm && (regStatus.isError || regStatus.isRefetching) && (
          <RegistrationFormErrorFallback
            retry={() => getRegForm.refetch()}
            loading={getRegForm.isRefetching}
          />
        )}
        {step === Step.LoadingForm && regStatus.isLoading && (
          <div className="flex-center">
            <InlineLoader />
          </div>
        )}
        {step === Step.LoadingForm && regStatus.isFetched && (
          <div className="flex-center">
            <Button onClick={nextStep}>{t('shared.continue')}</Button>
          </div>
        )}

        {step >= Step.AgeCheck && (
          <div>
            <Title
              passed={step > Step.AgeCheck}
              onBack={() => {
                navigation.prevStep();
                setValue('isAdult', null);
                clearAgeCheck();
              }}
            >
              {t('register:age_check.title')}
            </Title>
            <Foldable show={step === Step.AgeCheck}>
              <AgeCheck navigation={navigation} />
            </Foldable>
          </div>
        )}

        {step >= Step.PricingTable && (
          <div>
            <Title passed={step > Step.PricingTable}>{t('register:admission.choose_level')}</Title>
            <Foldable show={step === Step.PricingTable}>
              <Pricing navigation={navigation} />
            </Foldable>
            <Foldable show={step > Step.PricingTable}>
              <PricingPassed />
            </Foldable>
          </div>
        )}

        {step >= Step.Addons && (
          <div>
            <Title passed={step > Step.Addons} onBack={() => navigation.prevStep()}>
              {t('register:addons.choose_addons')}
            </Title>
            <Foldable show={step === Step.Addons}>
              <Addons navigation={navigation} />
            </Foldable>
          </div>
        )}

        {step >= Step.AgreementCheck && (
          <div>
            <Title passed={step > Step.AgreementCheck} onBack={() => navigation.prevStep()}>
              {t('register:agreement.title')}
            </Title>
            <Foldable show={step === Step.AgreementCheck}>
              <Agreement navigation={navigation} />
            </Foldable>
          </div>
        )}

        {step >= Step.Platform && (
          <div>
            <Title passed={step > Step.Platform} onBack={() => setStep(Step.AgreementCheck)}>
              {t('register:additional_information.title')}
            </Title>
            <Foldable show={step >= Step.Platform && step <= Step.Email}>
              <AdditionalInformation navigation={navigation} />
            </Foldable>
          </div>
        )}

        {step >= Step.Confirmation && step <= Step.Payment && (
          <div>
            <Title passed={step > Step.Confirmation} onBack={() => setStep(Step.Platform)}>
              {t('register:payment.title')}
            </Title>

            <RegistrationSummary
              registrationData={processRegForm(getValues())}
              submittingForm={postRegForm.isPending || checkoutCreate.isPending}
              onSubmit={submit}
              navigation={navigation}
            >
              <div className="fluid-paragraph mt-5">
                <Payment restartRegistration={restartRegistration} navigation={navigation} />
              </div>
            </RegistrationSummary>
          </div>
        )}

        {step >= Step.Discord && (
          <div>
            <Title passed={step > Step.Discord} onBack={() => navigation.prevStep()}>
              {t('register:discord.join.title')}
            </Title>
            {
              // TODO: is discord linking ever off?
              enableDiscordLink
                ? step === Step.Discord && <Discord navigation={navigation} />
                : step === Step.Discord && <JoinOurDiscord navigation={navigation} />
            }
          </div>
        )}

        {step === Step.Complete && (
          <div>
            <RegistrationComplete navigation={navigation} />
            {import.meta.env.DEV && (
              <div className="d-flex justify-content-center">
                <Button onClick={() => nukeReg.mutate()} disabled={nukeReg.isPending}>
                  [DEV-ONLY] Reset registration
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export const Route = createFileRoute('/register')({
  component: () => (
    <RegisterLayout title="registration" bannerTitle="shared.navigation.register" noLogin>
      <Registration />
    </RegisterLayout>
  ),
});
